import {useMutation} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {UploadTarget} from '@/hooks/sgsst/evidencias/use-evidence'
import type {components} from '@/lib/api-schema'
import {mintLicenciaUploadTarget, useLicenciaMutations, type LicenciaCategoria} from '@/hooks/inci/consola/use-licencia'
import {createRosterHooks} from '@/hooks/common/use-roster'

export type Admin = components['schemas']['AdminRead']
export type AdminCreateResult = components['schemas']['AdminCreateRead']
export type AdminLicencia = components['schemas']['AdminLicenciaRead']

export const ADMINS_PAGE_SIZE = 20

const roster = createRosterHooks<Admin>('/admin/admins', 'admins')

export function useAdmins(params: {page: number; search: string}, enabled = true) {
  return roster.useList(params, ADMINS_PAGE_SIZE, enabled)
}

export const useDeactivateAdmin = roster.useDeactivate
export const useReactivateAdmin = roster.useReactivate

export function useCreateAdmin() {
  const invalidate = roster.useInvalidate()
  return useMutation({
    mutationFn: async (input: {
      cedula: string
      name: string
      last_name: string
      email: string
      phone?: string
      confirmar_identidad?: boolean
    }) => (await api.post<AdminCreateResult>('/admin/admins', input)).data,
    onSuccess: invalidate,
  })
}

export function useUpdateAdmin() {
  const invalidate = roster.useInvalidate()
  return useMutation({
    mutationFn: async ({
      userId,
      ...input
    }: {
      userId: string
      name?: string
      last_name?: string
      email?: string | null
      phone?: string | null
    }) => (await api.patch<Admin>(`/admin/admins/${userId}`, input)).data,
    onSuccess: invalidate,
  })
}

export async function mintAdminLicenciaUploadTarget(userId: string, file: File): Promise<UploadTarget> {
  return mintLicenciaUploadTarget(`/admin/admins/${userId}/licencia`, file)
}

type AdminLicenciaSaveInput = {
  userId: string
  numero: string
  categoria: LicenciaCategoria
  vigencia: string
  doc_key: string
}

export function useAdminLicenciaMutations() {
  const invalidate = roster.useInvalidate()
  return useLicenciaMutations<AdminLicencia, AdminLicenciaSaveInput>(
    (userId) => `/admin/admins/${userId}/licencia`,
    invalidate
  )
}
